import React, { useState } from "react";
import { Loader2, Plus, X } from "lucide-react";
import UserApiService from "../../../services/apiServices/UserApiService";
import "./styles/UserByPhoneInput.css";

export interface ResolvedUser {
  userId: string;
  userName?: string;
  phone: string;
}

interface UserByPhoneInputProps {
  /** When set, render as a "locked" chip instead of the input. */
  resolved: ResolvedUser | null;
  onResolved: (user: ResolvedUser) => void;
  onCleared: () => void;
  placeholder?: string;
  /** Phone numbers already added — prevents duplicates. */
  excludePhones?: string[];
  duplicateMessage?: string;
  /** Icon for the lookup button. Defaults to Plus (the "add" affordance used
   *  by OwnerSelector/CollaboratorInput); pass a different icon where the
   *  action reads more like "confirm" than "add" (e.g. a single owner field). */
  confirmIcon?: React.ReactNode;
}

const UserByPhoneInput: React.FC<UserByPhoneInputProps> = ({
  resolved,
  onResolved,
  onCleared,
  placeholder = "10-digit phone number",
  excludePhones,
  duplicateMessage = "Already added.",
  confirmIcon,
}) => {
  const [input, setInput] = useState("");
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleLookup = async () => {
    const trimmed = input.trim();
    if (!/^\d{10}$/.test(trimmed)) {
      setError("Enter a valid 10-digit phone number.");
      return;
    }
    if (excludePhones?.includes(trimmed)) {
      setError(duplicateMessage);
      return;
    }

    setIsLookingUp(true);
    setError(null);
    try {
      const response = await UserApiService.getProfileByPhone(trimmed);
      const isOk = response?.generalProcessingStatus?.processingStatus === "OK";
      const profile = isOk ? response?.contents?.[0] : null;
      if (!profile?.userId) {
        setError("No user found with this phone number.");
        return;
      }
      onResolved({
        userId: profile.userId,
        userName: profile.userName,
        phone: trimmed,
      });
      setInput("");
    } catch {
      setError("Couldn't verify this number. Please try again.");
    } finally {
      setIsLookingUp(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (!isLookingUp) handleLookup();
    }
  };

  if (resolved) {
    return (
      <span className="user-phone-chip">
        <span className="user-phone-chip__info">
          {resolved.userName && (
            <span className="user-phone-chip__name">{resolved.userName}</span>
          )}
          <span className="user-phone-chip__phone">{resolved.phone}</span>
        </span>
        <button
          type="button"
          className="user-phone-chip__remove"
          onClick={onCleared}
          aria-label="Clear selection"
        >
          <X size={12} />
        </button>
      </span>
    );
  }

  return (
    <div className="user-phone-input">
      <div className="user-phone-input__row">
        <input
          type="tel"
          className="user-phone-input__field"
          placeholder={placeholder}
          value={input}
          maxLength={10}
          disabled={isLookingUp}
          onChange={(e) => {
            setInput(e.target.value.replace(/\D/g, ""));
            setError(null);
          }}
          onKeyDown={handleKeyDown}
        />
        <button
          type="button"
          className="user-phone-input__add"
          onClick={handleLookup}
          disabled={input.length !== 10 || isLookingUp}
          aria-label="Look up user"
        >
          {isLookingUp ? (
            <Loader2 size={18} className="user-phone-input__spinner" />
          ) : (
            confirmIcon ?? <Plus size={18} />
          )}
        </button>
      </div>
      {error && <p className="user-phone-input__error">{error}</p>}
    </div>
  );
};

export default UserByPhoneInput;
